import {
  Button,
  Divider,
  Form,
  Modal,
  notification,
  Popconfirm,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  CfdiComplementInfo,
  multiAssignComplement,
  searchCfdis,
} from "api/generated";
import { querySerializer } from "common/helpers/queryUtils";
import { useDebounce } from "hooks/commons/usePaginationTable";
import { useClient } from "hooks/useClient";
import { Key, useCallback, useEffect, useState } from "react";
import ResponsiveCol from "../form/ResponsiveCol";
import { FormItem } from "../form/FormItem";
import Search from "antd/es/input/Search";
import CFDIsTable from "./CFDIsTable";
import { COMPLEMENT_PARENT } from "routes/cfdiComplements/ComplementsScreen";
import { handleAxiosError } from "common/helpers/generalUtils";

interface AssignCFDIProps {
  onCancel: () => void;
  onSuccess: () => void;
  selectedKeys: Key[];
  type: "traspaso" | "venta" | "compra";
  parentType: COMPLEMENT_PARENT;
}

function AssignCFDI({
  onCancel,
  onSuccess,
  selectedKeys,
  type,
  parentType,
}: AssignCFDIProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { client } = useClient();
  const [CFDIs, setCFDIs] = useState<CfdiComplementInfo[]>([]);
  const [filterText, setFilterText] = useState<string>();
  const [selectedCFDI, setSelectedCFDI] = useState<CfdiComplementInfo>();

  const debouncedFilterText = useDebounce(filterText, 500);

  const reloadTable = useCallback(() => {
    setIsLoading(true);
    searchCfdis({
      client,
      querySerializer: querySerializer,
      query: {
        query: debouncedFilterText,
        type,
      },
    })
      .then(({ data }) => {
        setCFDIs(data ?? []);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [client, debouncedFilterText, type]);

  // useEffect principal
  useEffect(() => {
    reloadTable();
  }, [reloadTable]);

  function handleOnConfirm() {
    if (!selectedCFDI || selectedKeys.length === 0) {
      return;
    }
    setIsLoading(true);
    multiAssignComplement({
      client,
      body: {
        movementIds: selectedKeys.map((key) => `${key}`),
        parentType,
        type: "CFDI",
        cfdiId: selectedCFDI.id ?? "",
      },
    })
      .then(({ data }) => {
        notification.success({
          message: "Asignación exitosa",
          description: `${data}`,
        });
        onSuccess();
      })
      .catch((err) => handleAxiosError(err, "Error asignando CFDIs"))
      .finally(() => {
        setIsLoading(false);
      });
  }

  const DoubleConfirmation = (
    <Popconfirm
      title={`Asignación de CFDI a ${type}s`}
      description={
        <div
          style={{
            padding: "1rem",
            textAlign: "center",
            display: "table-caption",
          }}
        >
          <Typography.Text>
            ¿Está seguro de realizar la asignación del folio fiscal{" "}
          </Typography.Text>
          <Tag color="blue">
            {selectedCFDI?.fiscalId
              ? selectedCFDI.fiscalId.toUpperCase()
              : "N/A"}
          </Tag>
          <Typography.Text>
            a {selectedKeys.length} {type}s?
          </Typography.Text>
        </div>
      }
      onConfirm={handleOnConfirm}
      okText="Sí"
      cancelText="No"
    >
      <Button key="submit" size="large" type="primary" disabled={!selectedCFDI}>
        Asignar
      </Button>
    </Popconfirm>
  );

  return (
    <div>
      <Modal
        onCancel={() => {
          onCancel();
        }}
        open={true}
        styles={{
          mask: {
            backdropFilter: "blur(10px)",
          },
        }}
        width="100%"
        height="100%"
        title={`Asignación de CFDI a ${type}s`}
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button key="back" onClick={onCancel} size="large">
              Cancelar
            </Button>

            {DoubleConfirmation}
          </Space>
        }
      >
        <Spin spinning={isLoading}>
          <Form layout="vertical" component={false}>
            <Row gutter={24}>
              <ResponsiveCol size="xl">
                <label>
                  Selecciona un CFDI de la lista a continuación para vincularlo
                  con las {`${type}s`} seleccionadas.
                </label>
              </ResponsiveCol>
              <Divider />
              <ResponsiveCol size="xl">
                <FormItem label="Búsqueda por descripcion, ius, cfdi">
                  <Search
                    onChange={(event) => {
                      setFilterText(event.target.value);
                    }}
                    value={filterText}
                  />
                </FormItem>
              </ResponsiveCol>
              <Divider />
              <ResponsiveCol size="xl">
                <CFDIsTable
                  CFDIs={CFDIs}
                  isLoading={isLoading}
                  onSelectionChange={(
                    _id,
                    selectedUUID: CfdiComplementInfo
                  ) => {
                    setSelectedCFDI(selectedUUID);
                  }}
                  type={type}
                />
              </ResponsiveCol>
            </Row>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
}

export default AssignCFDI;
